/* CTHK REVIEW PRESENTATION ASSETS: genuine Judge.me store-review source with CTHK-owned presentation. */

(() => {
  const WIDGET_SELECTOR = '.jdgm-cards-carousel';
  const SOURCE_HIDDEN_CLASS = 'cthk-native-review-source';
  const STAGE_ID = 'cthk-customer-voices';
  let initialised = false;

  /* CTHK CUSTOMER VOICES PORTRAIT RESPONSIVE CARDS 2026: responsive Customer Voices card count. */
  const visibleCount = () => {
    if (window.matchMedia('(min-width: 1800px)').matches) return 5;
    if (window.matchMedia('(min-width: 1360px)').matches) return 4;
    if (window.matchMedia('(min-width: 960px)').matches) return 3;
    if (window.matchMedia('(min-width: 641px)').matches) return 2;
    return 1;
  };
  const create = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const collectReviews = (widget) => {
    const unique = new Map();
    widget.querySelectorAll('.jdgm-card[data-review-id]').forEach((card) => {
      if (card.classList.contains('jdgm-clone-before') || card.classList.contains('jdgm-clone-after')) return;
      const id = card.dataset.reviewId;
      const name = card.querySelector('.jdgm-reviewer-name')?.textContent?.trim();
      const text = card.querySelector('.jdgm-text')?.textContent?.trim();
      const rating = card.querySelectorAll('.jdgm-star.jdgm--on').length;
      if (id && name && text && rating && !unique.has(id)) unique.set(id, { id, name, text, rating });
    });
    return [...unique.values()];
  };

  const buildReviewModal = () => {
    const modal = create('div', 'cthk-review-modal');
    const modalTitleId = 'cthk-review-modal-title';
    modal.hidden = true;
    modal.setAttribute('role', 'presentation');
    modal.setAttribute('aria-hidden', 'true');

    const backdrop = create('div', 'cthk-review-modal__backdrop');
    backdrop.setAttribute('aria-hidden', 'true');
    const dialog = create('section', 'cthk-review-modal__dialog');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', modalTitleId);
    const closeButton = create('button', 'cthk-review-modal__close', '×');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Close full customer review');
    const eyebrow = create('span', 'cthk-review-modal__eyebrow', 'Full customer review');
    const name = create('h2', 'cthk-review-modal__name');
    name.id = modalTitleId;
    const stars = create('div', 'cthk-review-modal__stars');
    const text = create('p', 'cthk-review-modal__text');
    dialog.append(closeButton, eyebrow, name, stars, text);
    modal.append(backdrop, dialog);
    document.body.append(modal);

    let opener = null;
    const getFocusables = () => [...dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hasAttribute('disabled'));
    const close = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cthk-review-modal-open');
      if (opener?.isConnected) {
        opener.setAttribute('aria-expanded', 'false');
        opener.focus();
      }
      opener = null;
    };
    const open = (review, trigger) => {
      opener = trigger;
      name.textContent = review.name;
      stars.textContent = '★'.repeat(review.rating);
      stars.setAttribute('aria-label', String(review.rating) + ' out of 5 stars');
      stars.setAttribute('role', 'img');
      text.textContent = review.text;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('cthk-review-modal-open');
      window.requestAnimationFrame(() => closeButton.focus());
    };
    closeButton.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target === backdrop) close();
    });
    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = getFocusables();
      if (!focusables.length) {
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    return { open };
  };

  const initialise = () => {
    if (initialised) return true;
    const widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget) return false;
    const reviews = collectReviews(widget);
    if (reviews.length < 3) return false;

    const modal = buildReviewModal();
    const stage = create('section');
    stage.id = STAGE_ID;
    stage.setAttribute('aria-label', 'Customer reviews from Christmas Tree Hong Kong');
    const shell = create('div', 'cthk-voices-shell');
    const header = create('header', 'cthk-voices-head');
    header.append(create('span', 'cthk-voices-eyebrow', 'CUSTOMER VOICES'));
    header.append(create('h2', 'cthk-voices-title', 'Christmas moments, shared by our customers'));
    header.append(create('p', 'cthk-voices-subtitle', 'Genuine reviews from Christmas Tree Hong Kong customers'));
    const grid = create('div', 'cthk-voices-grid');
    grid.setAttribute('aria-live', 'polite');
    const controls = create('div', 'cthk-voices-controls');
    const previous = create('button', 'cthk-voices-button', '‹');
    previous.type = 'button';
    previous.setAttribute('aria-label', 'Show previous customer reviews');
    const next = create('button', 'cthk-voices-button', '›');
    next.type = 'button';
    next.setAttribute('aria-label', 'Show next customer reviews');
    controls.append(previous, next);
    shell.append(header, grid, controls);
    stage.append(shell);

    let start = 0;
    let isAnimating = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const render = (direction = 0) => {
      const count = visibleCount();
      grid.replaceChildren();
      for (let offset = 0; offset < count; offset += 1) {
        const review = reviews[(start + offset + reviews.length) % reviews.length];
        const card = create('article', 'cthk-voice-card');
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', '0');
        card.setAttribute('aria-haspopup', 'dialog');
        card.setAttribute('aria-expanded', 'false');
        card.setAttribute('aria-label', 'Read full customer review from ' + review.name);
        card.append(create('h3', 'cthk-voice-name', review.name));
        const cardStars = create('div', 'cthk-voice-stars', '★'.repeat(review.rating));
        cardStars.setAttribute('aria-hidden', 'true');
        card.append(cardStars, create('p', 'cthk-voice-text', review.text));
        card.append(create('span', 'cthk-voice-read-more', 'Read full review'));
        const openReview = () => modal.open(review, card);
        card.addEventListener('click', openReview);
        card.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openReview();
          }
        });
        grid.append(card);
      }
      if (!direction || reducedMotion) return;
      const motionClass = direction > 0 ? 'cthk-voices-grid--enter-next' : 'cthk-voices-grid--enter-previous';
      grid.classList.remove('cthk-voices-grid--enter-next', 'cthk-voices-grid--enter-previous');
      void grid.offsetWidth;
      grid.classList.add(motionClass);
      window.setTimeout(() => {
        grid.classList.remove(motionClass);
        isAnimating = false;
      }, 560);
    };
    const move = (direction) => {
      if (isAnimating) return;
      isAnimating = !reducedMotion;
      start = (start + direction + reviews.length) % reviews.length;
      render(direction);
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    window.addEventListener('resize', () => render(), { passive: true });
    render();

    widget.before(stage);
    widget.classList.add(SOURCE_HIDDEN_CLASS);
    widget.setAttribute('aria-hidden', 'true');
    initialised = true;
    return true;
  };

  if (!initialise()) {
    const observer = new MutationObserver(() => { if (initialise()) observer.disconnect(); });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 12000);
  }
})();
